import React, { useEffect, useMemo, useState } from 'react';
import { PageHeader, EmptyState } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowRightLeft, Check, X, Trash2, Users2, Clock } from 'lucide-react';
import { fmtDateTime } from '@/lib/format';
import { useAuth } from '@/lib/auth';

function StatusBadge({ status }) {
  const cls = status === 'pending' ? 'bg-[hsl(var(--warning-soft))] text-[hsl(var(--warning))]'
    : status === 'approved' ? 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]'
    : 'bg-muted text-muted-foreground';
  return <span className={`text-[11px] px-2 py-0.5 rounded capitalize ${cls}`}>{status}</span>;
}

function DecisionDialog({ open, onOpenChange, title, description, onConfirm, confirmLabel = 'Confirm' }) {
  const [note, setNote] = useState('');
  useEffect(() => { if (!open) setNote(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Note (optional)</Label>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="decision-note" placeholder="Add context that will be logged with the decision" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onConfirm(note)} data-testid="decision-confirm">{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTransfersPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('pending');
  const [approveFor, setApproveFor] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    const params = tab === 'all' ? '' : `?status=${tab}`;
    const r = await api.get(`/transfer-requests${params}`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [tab]);

  const counts = useMemo(() => ({
    pending: items.filter((i) => i.status === 'pending').length,
    approved: items.filter((i) => i.status === 'approved').length,
    rejected: items.filter((i) => i.status === 'rejected').length,
  }), [items]);

  const approve = async (note) => {
    if (!approveFor) return;
    try {
      await api.post(`/transfer-requests/${approveFor.id}/approve`, { note });
      toast.success('Transfer approved');
      setApproveFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const reject = async (note) => {
    if (!rejectFor) return;
    try {
      await api.post(`/transfer-requests/${rejectFor.id}/reject`, { note });
      toast.success('Transfer rejected');
      setRejectFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const cancel = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try { await api.delete(`/transfer-requests/${id}`); toast.success('Cancelled'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <>
      <PageHeader
        title={isAdmin ? 'Company Transfer Requests' : 'My Transfer Requests'}
        description={isAdmin ? 'Approve or reject staff requests to reassign a client company to another team member.' : 'Track requests you have submitted or are being transferred to you.'}
        actions={
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="transfer-tabs">
              <TabsTrigger value="pending">Pending{counts.pending ? ` · ${counts.pending}` : ''}</TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {items.length === 0 ? (
          <EmptyState icon={ArrowRightLeft} title={isAdmin ? 'Nothing to review' : 'No requests yet'} description={isAdmin ? (tab === 'pending' ? 'No transfer requests are waiting for your decision.' : 'No requests match this filter yet.') : 'From the Companies page, click the transfer icon on a company card to request reassignment.'} />
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} data-testid={`transfer-row-${r.id}`}>
                    <TableCell className="font-medium">{r.company_name}</TableCell>
                    <TableCell>
                      <p className="text-sm">{r.from_user_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{r.from_user_email}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{r.to_user_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{r.to_user_email}</p>
                    </TableCell>
                    <TableCell className="text-sm max-w-xs">
                      <p className="line-clamp-2">{r.reason || '—'}</p>
                      {r.review_note && (
                        <p className="text-[11px] text-muted-foreground mt-1">Note by {r.reviewed_by_name}: {r.review_note}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      <Clock className="h-3 w-3 inline mr-1" />{fmtDateTime(r.requested_at)}
                    </TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {r.status === 'pending' && isAdmin && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setApproveFor(r)} data-testid={`approve-transfer-${r.id}`}>
                              <Check className="h-4 w-4 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setRejectFor(r)} data-testid={`reject-transfer-${r.id}`}>
                              <X className="h-4 w-4 mr-1" />Reject
                            </Button>
                          </>
                        )}
                        {r.status === 'pending' && !isAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => cancel(r.id)} data-testid={`cancel-transfer-${r.id}`}>
                            <Trash2 className="h-4 w-4 mr-1" />Cancel
                          </Button>
                        )}
                        {r.status === 'pending' && isAdmin && (
                          <Button size="icon" variant="ghost" onClick={() => cancel(r.id)} data-testid={`cancel-transfer-${r.id}`} title="Delete request">
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <DecisionDialog
        open={!!approveFor}
        onOpenChange={(o) => !o && setApproveFor(null)}
        title={`Approve transfer of ${approveFor?.company_name || ''}`}
        description={`Company will be moved from ${approveFor?.from_user_name || '—'} to ${approveFor?.to_user_name || '—'}. This updates assignments immediately.`}
        onConfirm={approve}
        confirmLabel="Approve transfer"
      />
      <DecisionDialog
        open={!!rejectFor}
        onOpenChange={(o) => !o && setRejectFor(null)}
        title={`Reject transfer of ${rejectFor?.company_name || ''}`}
        description={`Assignment remains with ${rejectFor?.from_user_name || '—'}.`}
        onConfirm={reject}
        confirmLabel="Reject request"
      />
    </>
  );
}
